import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const pendingAction = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.me();
      setUser(data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth callback, let AuthCallback handle it.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const _afterAuth = (u) => {
    setAuthModalOpen(false);
    const fn = pendingAction.current;
    pendingAction.current = null;
    if (fn && u) { try { fn(u); } catch (e) { /* noop */ } }
  };

  const login = async (body) => {
    const { data } = await api.login(body);
    setUser(data.user);
    _afterAuth(data.user);
    return data.user;
  };
  const signup = async (body) => {
    const { data } = await api.signup(body);
    setUser(data.user);
    _afterAuth(data.user);
    return data.user;
  };
  const logout = async () => {
    await api.logout();
    setUser(null);
  };

  // Open the sign-in popup; optional action runs once the user authenticates (email/password).
  const openAuthModal = (action) => { pendingAction.current = action || null; setAuthModalOpen(true); };
  const closeAuthModal = () => { pendingAction.current = null; setAuthModalOpen(false); };

  // Run `action` if signed in, otherwise prompt sign-in and run it after.
  const requireAuth = (action) => {
    if (user) { action?.(user); return true; }
    openAuthModal(action);
    return false;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, signup, logout, checkAuth,
      authModalOpen, openAuthModal, closeAuthModal, requireAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
